import React, { useState } from 'react';

import { Helmet } from 'react-helmet-async';

import { User, Lock, Eye, EyeOff, Send, ArrowLeft, KeySquare } from 'lucide-react'; // Added KeySquare
import './AdminLogin.css';
import logo from '../../assets/images/whitelogo.png';

const API_URL = process.env.REACT_APP_API_URL;

const AdminLogin = ({ onLogin }) => {
  const [currentView, setCurrentView] = useState('login'); // 'login' | 'request-otp' | 'signup'

  const [signupStep, setSignupStep] = useState('verify-id'); // 'verify-id' | 'details' 
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [employeeId, setEmployeeId] = useState(''); // State for the employee ID input

  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);


   const getPageMeta = () => {
    switch (currentView) {
      case 'signup':
        return {
          title: 'Create Admin Account | GN VISA',
          description: 'Create a new account for the GN VISA admin panel.',
        };
      case 'request-otp':
        return {
          title: 'Forgot Password | GN VISA',
          description: 'Reset your password for the GN VISA admin panel.',
        };
      case 'login':
      default:
        return {
          title: 'Admin Sign In | GN VISA',
          description: 'Sign in to access the GN VISA administrative dashboard.',
        };
    }
  };
  const { title, description } = getPageMeta();

  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  // --- NEW: Handler for Employee ID Verification ---
  const handleVerifyEmployeeId = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      if (!employeeId) throw new Error('Please enter your Employee ID.');
      
      const response = await fetch(`${API_URL}/api/auth/verify-employee-id`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.isValid) {
        setSuccess('ID Verified. Please complete your registration.');
        setSignupStep('details'); // Move to the next step
      } else {
        setError(data.error || 'Verification failed. Please check the ID and try again.');
      }
    } catch (err) {
      setError(err.message || 'Verification request failed.');
    } finally {
      setLoading(false);
    }
  };

  // --- No change to handleLogin ---
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      if (!formData.email || !formData.password) throw new Error('Please fill in all fields');
      const response = await fetch(`${API_URL}/api/auth/login/admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email.trim(), password: formData.password }),
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminUser', JSON.stringify(data.admin));
        if (onLogin) onLogin(data.admin, data.token);
      } else {
        setError(data.error || 'Invalid email or password.');
      }
    } catch (err) {
      setError(err.message || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  // --- Small change to handleSignup: send the verified employeeId ---
  const handleSignup = async (e) => {
    e.preventDefault();
    if (!agreeTerms) {
      setError('You must agree to the Terms & Conditions to sign up.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      if (!formData.name || !formData.email || !formData.password) throw new Error('Please fill in all fields');
      const response = await fetch(`${API_URL}/api/auth/signup/admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData.name.trim(),
          email: formData.email.trim(),
          password: formData.password,
          employeeId: employeeId, // Pass the verified Employee ID
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message || 'Signup successful! You can now log in.');
        setTimeout(() => switchView('login'), 2000);
      } else {
        setError(data.error || 'Signup failed.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- No change to handleRequestOtp ---
  const handleRequestOtp = async (e) => {
    // ... your existing logic ...
  };
  
  // --- Updated to reset signupStep ---
  const switchView = (view) => {
    setCurrentView(view);
    setError('');
    setSuccess('');
    setFormData({ name: '', email: '', password: '' });
    if (view === 'signup') {
      setSignupStep('verify-id'); // Always start signup from the verification step
      setEmployeeId('');
    }
  };
  
  // --- No change to renderLoginForm ---
  const renderLoginForm = () => (
    <>
      <h2>Sign In</h2>
      <p className="al-form-panel-subtitle">Welcome back! Please sign in to your account.</p>
      <form onSubmit={handleLogin} className="al-form">
        <div className="al-input-group">
          <label>E-mail Address</label>
          <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Enter your e-mail" required disabled={loading} />
        </div>
        <div className="al-input-group">
          <label>Password</label>
          <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleInputChange} placeholder="Enter your Password" required disabled={loading} />
          <span className="al-password-eye" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff /> : <Eye />}</span>
        </div>
        <div className="al-form-actions">
          <button type="button" className="al-link-button" onClick={() => switchView('request-otp')}>Forgot Password?</button>
          <button type="submit" className="al-submit-btn" disabled={loading}>{loading ? 'Signing In...' : 'Sign In'}</button>
        </div>
      </form>
    </>
  );

  // --- REWRITTEN to handle signup steps ---
  const renderSignupForm = () => {
    if (signupStep === 'verify-id') {
      return (
        <>
          <h2>Account Verification</h2>
          <p className="al-form-panel-subtitle">Please enter your Employee ID to proceed.</p>
          <form onSubmit={handleVerifyEmployeeId} className="al-form">
            <div className="al-input-group">
              <label>Employee ID</label>
              <input type="text" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} placeholder="Enter your Employee ID" required disabled={loading} />
            </div>
            <div className="al-form-actions">
              <button type="button" className="al-link-button al-secondary-action" onClick={() => switchView('login')}>Back to Sign In</button>
              <button type="submit" className="al-submit-btn" disabled={loading}>{loading ? 'Verifying...' : 'Verify ID'}</button>
            </div>
          </form>
        </>
      );
    }

    if (signupStep === 'details') {
      return (
        <>
          <h2>Create your account</h2>
          <p className="al-form-panel-subtitle">ID Verified! Complete your registration.</p>
          <form onSubmit={handleSignup} className="al-form">
            <div className="al-input-group">
              <label>Name</label>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter your name" required disabled={loading} />
            </div>
            <div className="al-input-group">
              <label>E-mail Address</label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Enter your e-mail" required disabled={loading} />
            </div>
            <div className="al-input-group">
              <label>Password</label>
              <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleInputChange} placeholder="Enter your Password" required disabled={loading} />
              <span className="al-password-eye" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff /> : <Eye />}</span>
            </div>
            <div className="al-terms-group">
              <input type="checkbox" id="terms" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} disabled={loading} />
              <label htmlFor="terms">By Signing Up, I agree with <a href="/terms">Terms & Conditions</a></label>
            </div>
            <div className="al-form-actions">
              <button type="button" className="al-link-button al-secondary-action" onClick={() => switchView('login')}>Sign In</button>
              <button type="submit" className="al-submit-btn" disabled={loading || !agreeTerms}>{loading ? 'Creating...' : 'Sign Up'}</button>
            </div>
          </form>
        </>
      );
    }
  };
  
  // --- No change to renderForgotPasswordForm ---
  const renderForgotPasswordForm = () => (
    <>
      <h2>Forgot Password</h2>
      <p className="al-form-panel-subtitle">Enter your email to receive a reset link.</p>
       <form onSubmit={handleRequestOtp} className="al-form">
          <div className="al-input-group">
              <label>E-mail Address</label>
              <input type="email" value={forgotPasswordEmail} onChange={(e) => setForgotPasswordEmail(e.target.value)} placeholder="Enter your registered e-mail" required disabled={loading} />
          </div>
          <div className="al-form-actions">
            <button type="button" className="al-link-button al-secondary-action" onClick={() => switchView('login')}>Back to Sign In</button>
            <button type="submit" className="al-submit-btn" disabled={loading}>{loading ? 'Sending...' : 'Send Reset Link'}</button>
          </div>
       </form>
    </>
  );

  return (
    <div className="al-page">

       <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
      </Helmet>

      <div className="al-card-wrapper">
        <div className="al-info-panel">
            <div className="al-logo-container">
              <img style={{width:"200px"}} src={logo} alt="GN Visa Logo"/>
            </div>
            <h3>Hello, welcome to!</h3>
            <p><b>GN VISA</b></p>
            <button className="al-switch-view-btn" onClick={() => switchView(currentView === 'login' ? 'signup' : 'login')}>
              {currentView === 'login' ? 'Create Account' : 'Sign In'} <ArrowLeft className="al-arrow-icon" />
            </button>
        </div>

        <div className="al-form-panel">
            {error && <div className="al-message al-error">{error}</div>}
            {success && <div className="al-message al-success">{success}</div>}
            
            {currentView === 'login' && renderLoginForm()}
            {currentView === 'signup' && renderSignupForm()}
            {currentView === 'request-otp' && renderForgotPasswordForm()}
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;